<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Sección - Tipo Componente
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Asignación de tipos de componente a secciones
      </p>
    </div>

    <!-- Selector de Sección -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
      <div class="p-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Seleccionar Sección
        </label>
        <select
          v-model="seccionSeleccionada"
          @change="onSeccionChange"
          class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
        >
          <option :value="null">Seleccione una sección...</option>
          <option v-for="seccion in secciones" :key="seccion.seccionId" :value="seccion">
            {{ seccion.nombreSeccion }}
          </option>
        </select>
      </div>
    </div>

    <!-- Tabla de Tipos de Componente asignados -->
    <div v-if="seccionSeleccionada" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Tipos de Componente en: {{ seccionSeleccionada.nombreSeccion }}
          </h2>
          <button
            @click="openModal()"
            class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Asignar Tipo
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">Tipo de Componente</th>
                <th class="px-4 py-2 font-bold">Descripción</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="relacion in tiposAsignados"
                :key="relacion.seccionTipoComponenteId"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700"
              >
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">
                  {{ relacion.tipo_componente?.nombreTipo || '-' }}
                </td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">
                  {{ relacion.tipo_componente?.descripcion || '-' }}
                </td>
                <td class="px-4 py-2">
                  <button @click="eliminarRelacion(relacion)" class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="tiposAsignados.length === 0">
                <td colspan="3" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay tipos de componente asignados a esta sección
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal para asignar tipo -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            Asignar Tipo de Componente
          </h3>
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Componente *
            </label>
            <select
              v-model="formData.tipoComponenteId"
              class="w-full px-3 py-2 rounded border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option :value="null">Seleccione un tipo...</option>
              <option v-for="tipo in tiposDisponibles" :key="tipo.tipoComponenteId" :value="tipo.tipoComponenteId">
                {{ tipo.nombreTipo }}
              </option>
            </select>
          </div>
        </div>

        <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            @click="closeModal"
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancelar
          </button>
          <button
            @click="asignar"
            :disabled="loading || !formData.tipoComponenteId"
            class="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition disabled:opacity-50"
          >
            {{ loading ? 'Asignando...' : 'Asignar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useCatalogosNew } from '../../composables/useCatalogosNew'

const {
  loading,
  secciones,
  tiposComponente,
  fetchSecciones,
  fetchTiposComponente,
  asignarTipoASeccion,
  obtenerTiposDeSeccion,
  desasignarTipoDeSeccion
} = useCatalogosNew()

const seccionSeleccionada = ref(null)
const tiposAsignados = ref([])
const showModal = ref(false)
const formData = ref({
  tipoComponenteId: null
})

onMounted(async () => {
  await Promise.all([
    fetchSecciones(),
    fetchTiposComponente()
  ])
})

const onSeccionChange = async () => {
  if (seccionSeleccionada.value) {
    tiposAsignados.value = await obtenerTiposDeSeccion(seccionSeleccionada.value.seccionId)
  }
}

const tiposDisponibles = computed(() => {
  if (!tiposAsignados.value) return tiposComponente.value
  
  const asignadosIds = tiposAsignados.value.map(r => r.tipoComponenteId)
  return tiposComponente.value.filter(t => !asignadosIds.includes(t.tipoComponenteId))
})

const openModal = () => {
  formData.value = {
    tipoComponenteId: null
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formData.value = {
    tipoComponenteId: null
  }
}

const asignar = async () => {
  try {
    await asignarTipoASeccion({
      seccionId: seccionSeleccionada.value.seccionId,
      tipoComponenteId: formData.value.tipoComponenteId
    })
    closeModal()
    await onSeccionChange()
  } catch (error) {
    console.error('Error al asignar:', error)
  }
}

const eliminarRelacion = async (relacion) => {
  if (confirm(`¿Está seguro de desasignar el tipo "${relacion.tipo_componente?.nombreTipo}"?`)) {
    try {
      await desasignarTipoDeSeccion(relacion.seccionTipoComponenteId)
      await onSeccionChange()
    } catch (error) {
      console.error('Error al eliminar:', error)
    }
  }
}
</script>
